import { useState } from 'react';

/**
 * Composition over time — stacked columns, with the vocabulary supplied.
 *
 * The series are a prop rather than a constant, and that is the whole point of
 * this file. It began as a verdict chart with GOOD/UNSURE/BAD hard-coded in the
 * legend and the tooltip. Reusing it for classifications by mapping predictions
 * onto the `good` key rendered a chart of pork shoulders labelled GOOD and
 * UNSURE — the exact wording the task separation exists to make impossible,
 * arriving through a component nobody thought of as carrying wording.
 *
 * The database refuses to store a verdict on a crate. This is the same rule
 * applied to a legend: a chart cannot name an outcome its caller did not.
 *
 * Colours are a STATUS palette where the caller passes one — fixed meanings,
 * always paired with a label, so nothing rests on hue alone. The amber/green
 * pair is deliberately bright; darkening it collapses their separation under
 * protanopia.
 *
 * Mark spec: columns capped at 24px, 4px rounded cap with a square baseline,
 * a 2px surface gap between stacked segments, hairline recessive gridlines.
 */

export interface ColumnSeries {
  /** Key into the bucket. */
  key: string;
  /** What it is called, in the caller's vocabulary. */
  label: string;
  color: string;
}

export interface ColumnBucket {
  t: string;
  avgLatencyMs: number;
  [series: string]: string | number;
}

const H = 220;
const PAD_L = 44;
const PAD_B = 26;
const PAD_T = 10;
const MAX_BAR = 24;
const GAP = 2; // surface gap between stacked segments

function niceMax(v: number): number {
  if (v <= 5) return 5;
  const mag = 10 ** Math.floor(Math.log10(v));
  return Math.ceil(v / mag) * mag;
}

function fmtTime(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

export function StackedColumns({
  buckets,
  series,
  emptyMessage = 'Nothing in this window yet.',
  ariaLabel = 'Composition over time',
}: {
  buckets: ColumnBucket[];
  series: readonly ColumnSeries[];
  emptyMessage?: string;
  ariaLabel?: string;
}) {
  const [hover, setHover] = useState<number | null>(null);

  if (buckets.length === 0) {
    return <div className="muted" style={{ padding: '32px 0' }}>{emptyMessage}</div>;
  }

  const valueOf = (b: ColumnBucket, key: string) => Number(b[key] ?? 0);
  const totals = buckets.map((b) => series.reduce((s, x) => s + valueOf(b, x.key), 0));
  const max = niceMax(Math.max(1, ...totals));
  const W = 720;
  const plotW = W - PAD_L;
  const plotH = H - PAD_B - PAD_T;
  const slot = plotW / buckets.length;
  const barW = Math.min(MAX_BAR, slot * 0.6);
  const y = (v: number) => PAD_T + plotH - (v / max) * plotH;
  const ticks = [0, max / 2, max];

  return (
    <div style={{ position: 'relative' }}>
      {/* Legend: identity is never colour-alone. */}
      <div className="row" style={{ gap: 16, marginBottom: 10 }}>
        {series.map((s) => (
          <span key={s.key} className="row" style={{ gap: 6 }}>
            <span
              style={{ width: 12, height: 12, borderRadius: 3, background: s.color, display: 'inline-block' }}
            />
            <span className="muted" style={{ fontSize: 13 }}>{s.label}</span>
          </span>
        ))}
      </div>

      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: H }} role="img" aria-label={ariaLabel}>
        {/* Recessive hairline gridlines + rounded y ticks */}
        {ticks.map((t) => (
          <g key={t}>
            <line x1={PAD_L} x2={W} y1={y(t)} y2={y(t)} stroke="var(--border)" strokeWidth={1} />
            <text x={PAD_L - 8} y={y(t) + 4} textAnchor="end" fontSize={11} fill="var(--text-dim)">
              {Math.round(t).toLocaleString()}
            </text>
          </g>
        ))}

        {buckets.map((b, i) => {
          const x = PAD_L + i * slot + (slot - barW) / 2;
          if (series.reduce((s, x2) => s + valueOf(b, x2.key), 0) === 0) return null;

          // Stack from the baseline up, in reverse legend order, so the first
          // series named sits on top where the eye lands.
          let cursor = 0;
          const segs = [...series]
            .reverse()
            .map((s) => ({ v: valueOf(b, s.key), color: s.color }))
            .filter((s) => s.v > 0)
            .map((s, idx, arr) => {
              const y0 = y(cursor);
              cursor += s.v;
              const y1 = y(cursor);
              const isTop = idx === arr.length - 1;
              // 2px surface gap between touching segments (not a stroke).
              const h = Math.max(1, y0 - y1 - (isTop ? 0 : GAP));
              return { ...s, y: y1 + (isTop ? 0 : GAP), h, isTop };
            });

          return (
            <g key={b.t} onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}>
              {/* Hit target wider than the mark */}
              <rect x={PAD_L + i * slot} y={PAD_T} width={slot} height={plotH} fill="transparent" />
              {segs.map((s, si) => (
                <rect
                  key={si}
                  x={x}
                  y={s.y}
                  width={barW}
                  height={s.h}
                  fill={s.color}
                  // 4px rounded data-end, square at the baseline
                  rx={s.isTop ? 4 : 0}
                  opacity={hover === null || hover === i ? 1 : 0.45}
                />
              ))}
            </g>
          );
        })}

        {/* Time axis: first, middle and last only — never a label per column. */}
        {[0, Math.floor(buckets.length / 2), buckets.length - 1]
          .filter((v, idx, arr) => arr.indexOf(v) === idx && buckets[v])
          .map((i) => (
            <text
              key={i}
              x={PAD_L + i * slot + slot / 2}
              y={H - 8}
              textAnchor="middle"
              fontSize={11}
              fill="var(--text-dim)"
            >
              {fmtTime(buckets[i]!.t)}
            </text>
          ))}
      </svg>

      {hover !== null && buckets[hover] && (
        <div
          className="panel"
          style={{
            position: 'absolute',
            top: 26,
            left: `${((PAD_L + hover * slot + slot / 2) / W) * 100}%`,
            transform: 'translateX(-50%)',
            padding: '10px 12px',
            pointerEvents: 'none',
            fontSize: 13,
            whiteSpace: 'nowrap',
            zIndex: 5,
          }}
        >
          <div className="muted" style={{ marginBottom: 4 }}>{fmtTime(buckets[hover]!.t)}</div>
          {series.map((s) => (
            <div key={s.key} className="row" style={{ gap: 6, justifyContent: 'space-between' }}>
              <span className="row" style={{ gap: 6 }}>
                <span style={{ width: 8, height: 8, borderRadius: 2, background: s.color }} />
                {s.label}
              </span>
              <strong style={{ fontFamily: 'var(--mono)' }}>{valueOf(buckets[hover]!, s.key)}</strong>
            </div>
          ))}
          <div className="muted" style={{ marginTop: 4 }}>
            {buckets[hover]!.avgLatencyMs} ms avg
          </div>
        </div>
      )}
    </div>
  );
}

/** The quality question's outcomes. Only ever used where verdicts exist. */
export const VERDICT_SERIES: readonly ColumnSeries[] = [
  { key: 'good', label: 'GOOD', color: 'var(--good)' },
  { key: 'unsure', label: 'UNSURE', color: 'var(--warn)' },
  { key: 'bad', label: 'BAD', color: 'var(--bad)' },
];

/**
 * The identity question's outcomes.
 *
 * Two, not three: a classification is either named or handed to a person. There
 * is no third state, and nothing here is ever "bad" — a crate of shoulder is not
 * a failure.
 */
export const CLASSIFICATION_SERIES: readonly ColumnSeries[] = [
  { key: 'predicted', label: 'Classified', color: 'var(--good)' },
  { key: 'rejected', label: 'Sent to a person', color: 'var(--warn)' },
];
